// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.

import type { ReactNode } from 'react'
import Box from '@mui/material/Box'
import Skeleton from '@mui/material/Skeleton'
import Button from '@mui/material/Button'
import Alert from '@mui/material/Alert'
import { friendlyError } from '../../api/errors'
import EmptyState from './EmptyState'

/**
 * D-UI-3 c6 — the only way a page says "loading", "failed" or "nothing here" (E-07; CareRx audit C13, O14,
 * E19). The rule that matters: EMPTY is shown only after a SUCCESSFUL load that returned nothing. Pending
 * shows a skeleton; a failure shows the reason with Retry; a failed RE-load keeps the last data on screen
 * under a warning — never an empty state, never an all-clear.
 */
export interface LoadStateQuery<T> {
  data: T | undefined
  isPending: boolean
  isError: boolean
  isSuccess: boolean
  error: unknown
  refetch: () => unknown
}

export interface LoadStateProps<T> {
  query: LoadStateQuery<T>
  /** When the loaded data counts as empty. Default: an empty array. */
  isEmpty?: (data: T) => boolean
  empty: ReactNode
  /** What failed, in the reader's words ("the holds", "the lab tracker"). */
  what?: string
  skeletonRows?: number
  children: (data: T) => ReactNode
}

const defaultEmpty = (d: unknown) => Array.isArray(d) && d.length === 0

export default function LoadState<T>({ query, isEmpty = defaultEmpty, empty, what = 'this list', skeletonRows = 4, children }: LoadStateProps<T>) {
  const retry = <Button size="small" color="inherit" onClick={() => { void query.refetch() }}>Retry</Button>
  if (query.data === undefined) {
    if (query.isError) return (
      <Alert severity="error" role="alert" action={retry}>
        Could not load {what} — nothing is shown. {friendlyError(query.error)}
      </Alert>
    )
    return (
      <Box aria-busy="true" aria-label={`Loading ${what}`}>
        {Array.from({ length: skeletonRows }, (_, i) => <Skeleton key={i} height={34} />)}
      </Box>
    )
  }
  const data = query.data
  return (
    <>
      {query.isError && (
        <Alert severity="warning" role="alert" action={retry} sx={{ mb: 1 }}>
          Could not refresh {what} — what you see may be out of date. {friendlyError(query.error)}
        </Alert>
      )}
      {/* A failed reload over EMPTY data shows the warning only: the children's own "nothing here" would be
          an all-clear nobody verified (Phase 1 review). */}
      {query.isError && isEmpty(data) ? null
        : query.isSuccess && isEmpty(data) ? (typeof empty === 'string' ? <EmptyState title={empty} /> : empty)
        : children(data)}
    </>
  )
}
